/**
 * Description item for marker info window
 */
interface MarkerDescription {
  /**
   * Title text for the description item
   */
  title: string;
  
  /**
   * Data text for the description item
   */
  data: string | number;
}

/**
 * Options pour la création d'un marqueur de ville
 */
type MarkerOptions = {
  /**
   * ID de l'élément de la carte
   * @default undefined
   */
  itemId?: string | number | undefined;

  /**
   * La couleur de l'épingle dans n'importe quel format CSS valide
   * @default "oklch(74.04% 0.1498 141.4)"
   */
  color?: string;
  
  /**
   * La classe pour la div externe
   * @default "hg-marker"
   */
  markerClass?: string;
  
  /**
   * La classe pour la div de l'épingle
   * @default "hg-marker-pin"
   */
  pinClass?: string;
  
  /**
   * La classe pour l'élément SVG
   * @default "hg-marker-pinSvg"
   */
  svgClass?: string;
  
  /**
   * La classe pour la fenêtre d'info
   * @default "hg-marker-infoWindow"
   */
  infoWindowClass?: string;
  
  /**
   * Le contenu HTML à afficher dans la fenêtre d'info
   * @default ""
   */
  infoContent?: string;

  /**
   * Titre à afficher dans la fenêtre d'info
   * @default ""
   */
  infoTitle?: string;
  
  /**
   * Classe pour le conteneur de titre dans la fenêtre d'info
   * @default "hg-marker-infoTitle" 
   */
  infoTitleClass?: string;
  
  /**
   * Classe pour le conteneur de contenu dans la fenêtre d'info
   * @default "hg-marker-infoContent"
   */
  infoContentClass?: string;

  /**
   * Fonction de rappel pour l'événement mouseenter
   * @default () => {}
   */
  mouseenterHandler?: (event: MouseEvent) => void;

  /**
   * Fonction de rappel pour l'événement mouseleave
   * @default () => {}
   */
  mouseleaveHandler?: (event: MouseEvent) => void;

  /**
   * Liste des descriptions à afficher dans la fenêtre d'info
   * @default []
   */
  descriptions?: MarkerDescription[];

  /**
   * Classe pour le bloc de description
   * @default "hg-marker-descriptionBlock"
   */
  descriptionBlockClass?: string;

  /**
   * Classe pour le titre de description
   * @default "hg-marker-descriptionTitle"
   */
  descriptionTitleClass?: string;

  /**
   * Classe pour les données de description
   * @default "hg-marker-descriptionData"
   */
  descriptionDataClass?: string;
}

/**
 * Crée un élément marqueur de ville avec une fenêtre d'info suivant cette structure :
 * 
 * <div class="hg-marker" tabindex="0">
 *   <div class="hg-marker-pin">
 *     <svg class="hg-marker-pinSvg" width="43.074017" height="35.1297" viewBox="0 0 43.074017 35.1297" fill="none" version="1.1">
 *       <path d="m 38.5846,10.6263 c 0.048,-1.7073 0.048,-3.8824 -1.3161,-5.1459 -1.3641,-1.2634 -4.1402,-1.4546 -5.8435,-1.5502 -3.0846,-0.1468 -6.1384,-0.2424 -9.2058,-0.2424 -3.2628,0 -6.4776,0.0956 -9.7404,0.1434 -1.8987,0.1468 -4.7743,0.3892 -6.1863,1.9429 -1.3161,1.4546 -1.46,4.0737 -1.508,5.822 v 11.8385 c 0.048,1.9429 0.1919,4.562 1.7513,6.0678 1.5595,1.5059 4.1882,1.5502 6.0869,1.6493 h 18.0208 c 0.3723,-0.0388 0.7465,-0.0547 1.1208,-0.0478 3.5575,-0.1947 5.2129,-1.3113 6.0389,-2.8171 0.73,-1.4546 0.9768,-3.6366 1.0727,-7.0342 H 20.9511 v -3.4146 h 22.113 c 0.048,5.5795 -0.048,8.9771 -0.9734,11.4527 -0.6328,1.8049 -1.9117,3.3137 -3.5918,4.2376 -1.8987,1.0687 -3.6535,1.1643 -5.7476,1.3112 -3.2628,0.1468 -6.5119,0.2902 -9.7884,0.2902 -3.8488,0 -7.6463,-0.1434 -11.5432,-0.2902 -0.7779,-0.0478 -1.5594,-0.099 -2.3991,-0.1947 -4.3355,-0.5326 -6.1863,-2.3287 -7.1596,-4.0258 -1.3161,-2.3322 -1.5115,-4.9512 -1.7137,-7.4746 -0.0514,-1.7961 -0.1473,-3.5888 -0.1473,-5.4327 0,-2.2332 0.1473,-4.4186 0.2913,-6.6483 0.048,-0.6317 0.1473,-1.2634 0.1953,-1.9429 0.4387,-3.0083 1.5115,-5.5796 4.1882,-7.2288 2.1421,-1.2635 4.1402,-1.4069 6.577,-1.6015 3.0195,-0.1468 6.0869,-0.2902 9.1064,-0.2902 3.9448,0 7.8828,0.1434 11.8345,0.2902 3.6055,0.3415 7.4509,0.8741 9.4491,4.4629 1.0282,1.8951 1.1687,3.8347 1.2646,5.8732 z" fill="oklch(74.04% 0.1498 141.4)" stroke="oklch(74.04% 0.1498 141.4)"></path>
 *     </svg>
 *   </div>
 *   <div class="hg-marker-infoWindow">
 *     <h3 class="hg-marker-infoTitle">Titre de l'info</h3>
 *     <div class="hg-marker-infoContent">
 *       <dl>
 *       </dl>
 *     </div>
 *   </div>
 * </div>
 *
 * @param options - Options de configuration
 * @returns L'élément marqueur de ville complet
 */
export function buildMarker(options: MarkerOptions = {}): HTMLDivElement {
  const {
    itemId = undefined,
    color = 'oklch(74.04% 0.1498 141.4)',
    markerClass = 'hg-marker',
    pinClass = 'hg-marker-pin',
    svgClass = 'hg-marker-pinSvg',
    infoWindowClass = 'hg-marker-infoWindow',
    infoTitleClass = 'hg-marker-infoTitle',
    infoContentClass = 'hg-marker-infoContent',
    infoContent = '',
    infoTitle = '',
    mouseenterHandler = undefined,
    mouseleaveHandler = undefined,
    descriptions = [],
    descriptionBlockClass = 'hg-marker-descriptionBlock',
    descriptionTitleClass = 'hg-marker-descriptionTitle',
    descriptionDataClass = 'hg-marker-descriptionData'
  } = options;
  
  // Création du conteneur principal avec tabindex pour le focus
  const marker = document.createElement('div');
  marker.className = markerClass;

  if(itemId) {
    marker.dataset.itemId = itemId.toString();
  }
 
  if(mouseenterHandler) {
    marker.addEventListener('mouseenter', mouseenterHandler);
  }

  if(mouseleaveHandler) {
    marker.addEventListener('mouseleave', mouseleaveHandler);
  }
  
  // Création du conteneur de l'épingle
  const pin = document.createElement('div');
  pin.className = pinClass;
  
  // Création de l'élément SVG
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('class', svgClass);
  svg.setAttribute('width', '43.074017');
  svg.setAttribute('height', '35.1297');
  svg.setAttribute('viewBox', '0 0 43.074017 35.1297');
  svg.setAttribute('fill', 'none');
  svg.setAttribute('version', '1.1');
  
  // Création de l'élément path
  const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  path.setAttribute('d', 'm 38.5846,10.6263 c 0.048,-1.7073 0.048,-3.8824 -1.3161,-5.1459 -1.3641,-1.2634 -4.1402,-1.4546 -5.8435,-1.5502 -3.0846,-0.1468 -6.1384,-0.2424 -9.2058,-0.2424 -3.2628,0 -6.4776,0.0956 -9.7404,0.1434 -1.8987,0.1468 -4.7743,0.3892 -6.1863,1.9429 -1.3161,1.4546 -1.46,4.0737 -1.508,5.822 v 11.8385 c 0.048,1.9429 0.1919,4.562 1.7513,6.0678 1.5595,1.5059 4.1882,1.5502 6.0869,1.6493 h 18.0208 c 0.3723,-0.0388 0.7465,-0.0547 1.1208,-0.0478 3.5575,-0.1947 5.2129,-1.3113 6.0389,-2.8171 0.73,-1.4546 0.9768,-3.6366 1.0727,-7.0342 H 20.9511 v -3.4146 h 22.113 c 0.048,5.5795 -0.048,8.9771 -0.9734,11.4527 -0.6328,1.8049 -1.9117,3.3137 -3.5918,4.2376 -1.8987,1.0687 -3.6535,1.1643 -5.7476,1.3112 -3.2628,0.1468 -6.5119,0.2902 -9.7884,0.2902 -3.8488,0 -7.6463,-0.1434 -11.5432,-0.2902 -0.7779,-0.0478 -1.5594,-0.099 -2.3991,-0.1947 -4.3355,-0.5326 -6.1863,-2.3287 -7.1596,-4.0258 -1.3161,-2.3322 -1.5115,-4.9512 -1.7137,-7.4746 -0.0514,-1.7961 -0.1473,-3.5888 -0.1473,-5.4327 0,-2.2332 0.1473,-4.4186 0.2913,-6.6483 0.048,-0.6317 0.1473,-1.2634 0.1953,-1.9429 0.4387,-3.0083 1.5115,-5.5796 4.1882,-7.2288 2.1421,-1.2635 4.1402,-1.4069 6.577,-1.6015 3.0195,-0.1468 6.0869,-0.2902 9.1064,-0.2902 3.9448,0 7.8828,0.1434 11.8345,0.2902 3.6055,0.3415 7.4509,0.8741 9.4491,4.4629 1.0282,1.8951 1.1687,3.8347 1.2646,5.8732 z');
  path.setAttribute('fill', color);
  path.setAttribute('stroke', color);
  
  // Création de la fenêtre d'info
  const infoWindow = document.createElement('div');
  infoWindow.className = infoWindowClass;
  
  // Ajout du titre si fourni
  if (infoTitle) {
    const titleElement = document.createElement('div');
    titleElement.className = infoTitleClass;
    titleElement.textContent = infoTitle;
    infoWindow.appendChild(titleElement);
  }
  
  // Ajout du contenu si fourni
  const contentElement = document.createElement('div');
  contentElement.className = infoContentClass;
  contentElement.innerHTML = infoContent;
  
  // Création et ajout de la liste de description seulement si des descriptions existent
  if (descriptions.length > 0) {
    const dlElement = document.createElement('dl');
    
    descriptions.forEach(desc => {
      const descriptionBlock = document.createElement('div');
      descriptionBlock.className = descriptionBlockClass;
      
      const descriptionTitle = document.createElement('dt');
      descriptionTitle.className = descriptionTitleClass;
      descriptionTitle.textContent = desc.title;
      
      const descriptionData = document.createElement('dd');
      descriptionData.className = descriptionDataClass;
      descriptionData.textContent = desc.data.toString();
      
      descriptionBlock.appendChild(descriptionTitle);
      descriptionBlock.appendChild(descriptionData);
      dlElement.appendChild(descriptionBlock);
    });
    
    contentElement.appendChild(dlElement);
  }
  
  infoWindow.appendChild(contentElement);

  // Assemblage des éléments
  svg.appendChild(path);
  pin.appendChild(svg);
  marker.appendChild(pin);
  marker.appendChild(infoWindow);
  
  return marker;
}

/**
 * Crée un marqueur de prix en forme de pilule pour les projets
 * @param price - Le prix à afficher
 * @param options - Options supplémentaires du marqueur
 * @returns L'élément marqueur de prix
 */
export function buildPriceMarker(price: string, options: Partial<MarkerOptions> = {}): HTMLDivElement {
  const {
    itemId = undefined,
    mouseenterHandler = undefined,
    mouseleaveHandler = undefined,
    infoTitle = '',
    descriptions = []
  } = options;

  // Création du conteneur principal
  const marker = document.createElement('div');
  marker.className = 'hg-price-marker';
  
  if (itemId) {
    marker.dataset.itemId = itemId.toString();
  }

  if (mouseenterHandler) {
    marker.addEventListener('mouseenter', mouseenterHandler);
  }

  if (mouseleaveHandler) {
    marker.addEventListener('mouseleave', mouseleaveHandler);
  }

  // Création du contenu prix
  const priceContent = document.createElement('div');
  priceContent.className = 'hg-price-marker-content';
  priceContent.textContent = price;

  // Création de la fenêtre d'info (si nécessaire)
  const infoWindow = document.createElement('div');
  infoWindow.className = 'hg-marker-infoWindow';
  
  if (infoTitle) {
    const titleElement = document.createElement('div');
    titleElement.className = 'hg-marker-infoTitle';
    titleElement.textContent = infoTitle;
    infoWindow.appendChild(titleElement);
  }
  
  if (descriptions.length > 0) {
    const contentElement = document.createElement('div');
    contentElement.className = 'hg-marker-infoContent';
    
    const dlElement = document.createElement('dl');
    descriptions.forEach(desc => {
      const descriptionBlock = document.createElement('div');
      descriptionBlock.className = 'hg-marker-descriptionBlock';
      
      const descriptionTitle = document.createElement('dt');
      descriptionTitle.className = 'hg-marker-descriptionTitle';
      descriptionTitle.textContent = desc.title;
      
      const descriptionData = document.createElement('dd');
      descriptionData.className = 'hg-marker-descriptionData';
      descriptionData.textContent = desc.data.toString();
      
      descriptionBlock.appendChild(descriptionTitle);
      descriptionBlock.appendChild(descriptionData);
      dlElement.appendChild(descriptionBlock);
    });
    
    contentElement.appendChild(dlElement);
    infoWindow.appendChild(contentElement);
  }

  // Assemblage des éléments
  marker.appendChild(priceContent);
  marker.appendChild(infoWindow);
  
  return marker;
}
